import React, { useEffect, useRef, useState } from 'react';
import { Activity, Shield, HardHat, Zap, ArrowRight, Box, Truck, Code, CheckCircle, Rocket, Factory, Microchip, Handshake, RefreshCcw, MessageSquare, Wrench, HelpCircle, Calendar, Users, DollarSign, Lightbulb, Lock, Eye, AlertTriangle, MapPin, FileText, Info, Brain, FolderOpen, PaintBucket, BookOpen, Receipt, Clock, BarChart3, Hammer, Plug, Home } from "lucide-react";
import { ClipboardText, Lightning, ShieldCheck, TrendUp, Wrench as PhosphorWrench, House, Eye as PhosphorEye, Warning, Question, CalendarBlank, UsersThree, CurrencyDollar, Lightbulb as PhosphorLightbulb, LockSimple, Activity as PhosphorActivity } from "phosphor-react";
import { cn } from '@/lib/utils';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link, useNavigate } from 'react-router-dom';
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Progress } from "@/components/ui/progress";
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from "@/components/ui/button";
import { useScrollHijack } from '@/hooks/useScrollHijack';
import WaitlistModal from './WaitlistModal';
import { InspectionBookingModal } from './InspectionBookingModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Features = () => {
  const featuresRef = useRef<HTMLDivElement>(null);
  const hijackSectionRef = useRef<HTMLDivElement>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [progressValue, setProgressValue] = useState(0);
  const [currentSprint, setCurrentSprint] = useState(1);
  const totalSprints = 3;
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("");
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("diy-help");

  const tabSubHeadlines: Record<string, string> = {
    "diy-help": "Access an easy to follow DIY guide or Professional help for Repairs, Home Improvements, and Maintenance",
    "reports": "Plan for the future with Guidance, Insights, and Roadmaps for your home",
    "documents": "Save and Organize important documents and details"
  };

  const services = [
    {
      id: "repair-or-reno",
      icon: <Wrench className="w-8 h-8 text-brand-green" />,
      headline: "Get Help With Repairs or Projects",
      description: "Become a DIY star with detailed guidance for every task and project, or discover local pros that can help. We help you from planning to completion.",
      path: "/home-improvement",
    },
    {
      id: "maintenance-checklist",
      icon: <CheckCircle className="w-8 h-8 text-brand-green" />,
      headline: "Stay On Top of Maintenance",
      description: "We'll generate a personalized checklist of tasks that will keep your home, healthy, safe, and extend the life of appliances.",
      path: "/maintenance",
    },
    {
      id: "new-home-report",
      icon: <HardHat className="w-8 h-8 text-brand-blue" />,
      headline: "Create My Home Report and Roadmap",
      description: "Upload a home inspection or book an inspection through House Beat to get a clear view of your home's condition and upcoming needs.",
      path: "/home-report-roadmap",
    }
  ];

  const { isHijacked, currentIndex } = useScrollHijack(hijackSectionRef, services.length);

  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact-info');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in');
          (entry.target as HTMLElement).style.opacity = '1';
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    if (featuresRef.current) {
      const elements = featuresRef.current.querySelectorAll('.feature-item');
      elements.forEach(el => {
        if (!el.classList.contains('animate-slide-in')) {
          (el as HTMLElement).style.opacity = '0';
          observer.observe(el);
        }
      });
    }
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    const animateProgress = () => {
      setProgressValue(0);
      interval = setInterval(() => {
        setProgressValue(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setCurrentSprint(prev => prev < totalSprints ? prev + 1 : 1);
              animateProgress();
            }, 500);
            return 100;
          }
          return prev + 2;
        });
      }, 100);
    };
    animateProgress();
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const sensorCaseStudies = [{
    image: "/lovable-uploads/843446fe-638e-4efb-b885-ed3cd505325a.png",
    title: "Firefighter Safety",
    description: "Advanced protective gear with gas, temperature, positioning, and motion sensors for safer emergency response."
  }, {
    image: "/lovable-uploads/5463c9c5-0946-4280-a14b-17636ff69a98.png",
    title: "Industrial Worker Protection",
    description: "Safety workwear with vibration, pressure, and heating sensors to prevent injuries and monitor environmental hazards."
  }, {
    image: "/lovable-uploads/c5f8ee24-9815-4ebe-b65d-6f3d449feb8b.png",
    title: "Sports Performance",
    description: "Smart athletic wear with temperature and pressure sensors that track hydration, foot strike patterns, and performance metrics."
  }];
  const stepFlowItems = [{
    icon: <Microchip className="h-10 w-10 text-gray-700" />,
    title: "Smart Home Technology",
    description: "Advanced technology to help you manage your home better"
  }, {
    icon: <Factory className="h-10 w-10 text-gray-700" />,
    title: "Vetted Off-the-Shelf Hardware",
    description: "Carefully selected components that complement our technology"
  }, {
    icon: <Handshake className="h-10 w-10 text-gray-700" />,
    title: "Vetted Production Partners",
    description: "Expert manufacturing partners for quality and reliability"
  }];
  const sprintPhases = [{
    name: "Planning",
    icon: <CheckCircle className="h-4 w-4" />
  }, {
    name: "Development",
    icon: <Code className="h-4 w-4" />
  }, {
    name: "Testing",
    icon: <Box className="h-4 w-4" />
  }, {
    name: "Review",
    icon: <RefreshCcw className="h-4 w-4" />
  }];

  return <>
      <section id="features" className="relative overflow-hidden py-6 md:py-10 w-full">
        {/* Mobile CTA Buttons - Only visible on mobile */}
        <div className="md:hidden px-4 pt-2 pb-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <a href="https://app.housebeat.com/welcome" target="_blank" rel="noopener noreferrer">
              <Button 
                variant="brand"
                className="w-full min-h-[44px] px-6 py-3 shadow-lg hover:shadow-xl text-sm font-bold group"
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            
            <Link 
              to="/for-partners" 
              className="hidden md:block text-gray-600 hover:text-gray-800 transition-colors text-sm underline underline-offset-4"
            >
              For Insurance Carriers, Municipalities, and Utilities.
            </Link>
          </div>
        </div>

        <div className="w-full px-4 sm:px-6 lg:px-8 py-4" ref={featuresRef}> 
          
          {/* How House Beat's Membership Works Section */}
          <div className="relative overflow-hidden pb-12">
            <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  How Your Free Membership Works<span className="text-green-500">.</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-brand-green text-xl font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Set-up your home in minutes<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm">Answer a few questions to build your personalized plan. We'll recommend maintenance tasks, predict replacements, and help you with repairs or planning projects.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-brand-blue text-xl font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Customize your home's plan<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm">Access DIY guidance or book services when you need them through our text concierge. We charge a fee of $8.99+tax only when you book a service.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-purple-600 text-xl font-bold">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Take Control and find the help you need<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm">Your dashboard and access to our Concierge makes it easy to find the answers, help, and guidance you need to prevent breakdowns and take better care of your home, and wallet.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Onboarding Section - Tabbed Interface */}
          <div className="relative overflow-hidden pb-8 pt-12 border-t border-gray-200" id="get-started">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* White background header area */}
              <div className="text-center mb-6 max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  Everything You Need for Confident Homeownership<span className="text-green-500">.</span>
                </h2>
                <p className="mt-3 text-lg text-on-gradient">Choose a path to get started</p>
              </div>

              {/* Content cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-brand-green" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Stay ahead of your home<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm mb-4 flex-grow">Build a personalized maintenance plan, get timely reminders, helpful DIY guidance, book home services and more.</p>
                  <a href="https://app.housebeat.com/welcome?path=maintenance" target="_blank" rel="noopener noreferrer">
                    <Button variant="brand" size="sm" className="w-full">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Wrench className="w-6 h-6 text-brand-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Something broken? Get it fixed<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm mb-4 flex-grow">Describe the problem and House Beat will help find you a trusted pro, handle the scheduling, and keep you updated.</p>
                  <a href="https://app.housebeat.com/welcome?path=repair" target="_blank" rel="noopener noreferrer">
                    <Button variant="brand" size="sm" className="w-full">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Book home services<span className="text-green-500">.</span></h3>
                  <p className="text-muted-foreground text-sm mb-4 flex-grow">Furnace tune-ups, gutter cleaning, and more — get quotes, schedule, and pay on House Beat.</p>
                  <a href="https://app.housebeat.com/welcome?path=services" target="_blank" rel="noopener noreferrer">
                    <Button variant="brand" size="sm" className="w-full">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <Hammer className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Ready to upgrade your home<span className="text-green-500">?</span></h3>
                  <p className="text-muted-foreground text-sm mb-4 flex-grow">Request quotes from a network of vetted pros for any renovation or improvement project, big or small.</p>
                  <a href="https://app.housebeat.com/welcome?path=projects" target="_blank" rel="noopener noreferrer">
                    <Button variant="brand" size="sm" className="w-full">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>

              <InspectionBookingModal open={isInspectionModalOpen} onOpenChange={setIsInspectionModalOpen} />
            </div>
          </div>

        </div>
      </section>

      <WaitlistModal open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} featureTitle={selectedService} />
        
      <section className="relative overflow-hidden w-full">  
        {/* What House Beat Delivers section with background */}
        <div className="py-16">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 feature-item">
              <div className="inline-block mb-2 px-3 py-1 bg-gray-800 text-white rounded-full text-sm font-semibold shadow-sm">
                House Beat Delivers
              </div>
              <h3 className="text-3xl md:text-4xl font-bold">Home Management Made Simple<span className="text-green-500">.</span></h3>
              <p className="text-sm font-semibold text-gray-600 mt-4 max-w-2xl mx-auto">
                Reminders, Tracking, DIY Guidance, Hiring made easy, and a digital binder for your home.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-green-400 rounded-full flex items-center justify-center shadow-lg [&_svg]:w-2.5 [&_svg]:h-2.5 md:[&_svg]:w-3.5 md:[&_svg]:h-3.5">
                      <div className="text-white">
                        <PhosphorActivity weight="bold" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Home Improvement Project Tracker</h4>
                    <p className="text-sm text-gray-600 text-left">Keep track of your wish list, projects, and their progress. We make it easy to get quotes and plan like a pro.</p>
                  </div>
                </div>
              </div>
              
              
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-red-400 rounded-full flex items-center justify-center shadow-lg [&_svg]:w-2.5 [&_svg]:h-2.5 md:[&_svg]:w-3.5 md:[&_svg]:h-3.5">
                      <div className="text-white">
                        <PhosphorLightbulb weight="bold" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Maintenance Reminders & How-To Guides</h4>
                    <p className="text-sm text-gray-600 text-left">Never miss important maintenance with timely reminders and guides.</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-blue-400 rounded-full flex items-center justify-center shadow-lg">
                      <div className="text-white">
                        <MessageSquare className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Your Home's Concierge</h4>
                    <p className="text-sm text-gray-600 text-left">Ask questions, request quotes, book service and repairs simply by texting your Concierge, or requesting concierge support in your dashboard.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Centered row with two additional boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-blue-400 rounded-full flex items-center justify-center shadow-lg">
                      <div className="text-white">
                        <Handshake className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Get Quotes, Book Pros, and Pay</h4>
                    <p className="text-sm text-gray-600 text-left">Need a repair, upgrade, or project done? Request quotes, compare options, schedule the job, and even pay — all through your dashboard.</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-pink-400 rounded-full flex items-center justify-center shadow-lg">
                      <div className="text-white">
                        <FileText className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Your Home's Digital Binder</h4>
                    <p className="text-sm text-gray-600 text-left">Store receipts, warranties, reno plans, appliance manuals, and even paint colors — all in one secure, easy-to-find spot.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 h-full flex flex-col gap-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 md:w-7 md:h-7 bg-purple-400 rounded-full flex items-center justify-center shadow-lg [&_svg]:w-2.5 [&_svg]:h-2.5 md:[&_svg]:w-3.5 md:[&_svg]:h-3.5">
                      <div className="text-white">
                        <LockSimple weight="bold" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 text-left mb-2">Replacement & Lifecycle Tracking</h4>
                    <p className="text-sm text-gray-600 text-left">Monitor recall alerts, life expectancy and condition of major systems and appliances.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col items-center justify-center gap-2 mt-8">
              <a href="https://app.housebeat.com/welcome" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="brand"
                  size="lg"
                  className="shadow-lg hover:shadow-xl group font-semibold"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <p className="text-sm text-on-gradient text-center">
                Available to all homeowners in Ontario, Manitoba, and Alberta.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>;
};
export default Features;
